import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Plus, X, Check, Calendar, Clock, MapPin, Globe, Lock,
  Video, ExternalLink, CalendarPlus, Users, Loader2, Link as LinkIcon
} from 'lucide-react';
import { API, BACKEND_URL } from './shared';

const CalendarTab = ({ communityId, events, isAdmin, isMember, onEventCreated, onEventsRefresh }) => {
  const [showCreateEvent, setShowCreateEvent] = useState(false);

  const handleRsvp = async (eventId) => {
    try {
      const response = await axios.post(`${API}/communities/events/${eventId}/rsvp`, {}, { withCredentials: true });
      onEventsRefresh();
    } catch (error) { console.error(error); }
  };

  const downloadIcs = (eventId) => {
    window.open(`${API}/communities/events/${eventId}/ics`, '_blank');
  };

  return (
    <div className="space-y-5">
      {isAdmin && (
        <div className="bg-white rounded-2xl shadow-lg p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Calendar className="h-5 w-5 text-purple-500" />Evenements de la communaute</h3>
            <Button size="sm" onClick={() => setShowCreateEvent(true)} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white" data-testid="create-community-event-btn"><Plus className="h-4 w-4 mr-1" />Creer</Button>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun evenement</h3>
          <p className="text-gray-500">{isAdmin ? "Creez votre premier evenement !" : "Les evenements apparaitront ici."}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {[...events].sort((a, b) => {
            const dateA = new Date(a.date + (a.time ? `T${a.time}` : ''));
            const dateB = new Date(b.date + (b.time ? `T${b.time}` : ''));
            const isPastA = dateA < new Date(new Date().toDateString());
            const isPastB = dateB < new Date(new Date().toDateString());
            if (isPastA !== isPastB) return isPastA ? 1 : -1;
            return dateA - dateB;
          }).map(event => (
            <EventCard
              key={event.event_id}
              event={event}
              isMember={isMember}
              onRsvp={() => handleRsvp(event.event_id)}
              onDownloadIcs={() => downloadIcs(event.event_id)}
            />
          ))}
        </div>
      )}

      {showCreateEvent && (
        <CreateCommunityEventModal
          communityId={communityId}
          onClose={() => setShowCreateEvent(false)}
          onCreated={(e) => { onEventCreated(e); setShowCreateEvent(false); }}
        />
      )}
    </div>
  );
};


const EventCard = ({ event, isMember, onRsvp, onDownloadIcs }) => {
  const isPast = new Date(event.date) < new Date(new Date().toDateString());

  return (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow ${isPast ? 'opacity-70' : ''}`} data-testid={`event-${event.event_id}`}>
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0 shadow-md">
            <span className="text-xs font-medium uppercase">{new Date(event.date).toLocaleDateString('fr-FR', { month: 'short' })}</span>
            <span className="text-xl font-bold leading-none">{new Date(event.date).getDate()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-gray-900 text-lg">{event.title}</h4>
              {isPast && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Passe</span>}
            </div>
            {event.description && <p className="text-sm text-gray-600 mt-1 leading-relaxed">{event.description}</p>}
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500 flex-wrap">
              {event.time && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-purple-400" />{event.time}</span>}
              {event.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-pink-400" />{event.location}</span>}
              <span className="flex items-center gap-1.5">{event.visibility === 'public' ? <><Globe className="h-4 w-4 text-green-400" />Public</> : <><Lock className="h-4 w-4 text-gray-400" />Membres</>}</span>
              <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-blue-400" />{event.attendee_count || 0} participant{(event.attendee_count || 0) > 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* Meeting Link */}
        {event.meeting_url && (
          <div className="mt-4 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Video className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">
                  {event.meeting_type === 'jitsi' ? 'Reunion Jitsi Meet' : event.meeting_url?.includes('teams') ? 'Reunion Microsoft Teams' : event.meeting_url?.includes('zoom') ? 'Reunion Zoom' : event.meeting_url?.includes('meet.google') ? 'Google Meet' : 'Visioconference'}
                </p>
                <p className="text-xs text-indigo-500 font-medium">Cliquez pour rejoindre la reunion</p>
              </div>
              <a
                href={event.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-colors flex-shrink-0"
                data-testid={`join-meeting-${event.event_id}`}
              >
                <ExternalLink className="h-3.5 w-3.5" />Rejoindre
              </a>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          {isMember && !isPast && (
            <Button
              size="sm"
              variant={event.is_attending ? "default" : "outline"}
              className={event.is_attending ? "bg-green-600 hover:bg-green-700 text-white" : "border-purple-200 text-purple-600 hover:bg-purple-50"}
              onClick={onRsvp}
              data-testid={`rsvp-${event.event_id}`}
            >
              {event.is_attending ? <><Check className="h-4 w-4 mr-1" />Je participe</> : <><CalendarPlus className="h-4 w-4 mr-1" />Participer</>}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="border-gray-200 text-gray-600 hover:bg-gray-50"
            onClick={onDownloadIcs}
            data-testid={`download-ics-${event.event_id}`}
          >
            <Calendar className="h-4 w-4 mr-1" />Ajouter au calendrier
          </Button>
        </div>
      </div>
    </div>
  );
};


const CreateCommunityEventModal = ({ communityId, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '', description: '', date: '', time: '', location: '',
    visibility: 'members', meeting_type: 'jitsi', custom_meeting_url: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API}/communities/${communityId}/events`, form, { withCredentials: true });
      onCreated(response.data);
    } catch (error) { console.error(error); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-event-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">Nouvel evenement</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Nom de l'evenement" data-testid="event-title" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Decrivez l'evenement..." className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm" rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Heure</label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Lieu</label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Adresse ou salle" /></div>

          {/* Meeting Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Visioconference</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setForm({ ...form, meeting_type: 'jitsi', custom_meeting_url: '' })}
                className={`py-3 px-3 rounded-xl border-2 text-sm font-medium flex flex-col items-center gap-1.5 transition-all ${form.meeting_type === 'jitsi' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                data-testid="meeting-type-jitsi"
              >
                <Video className="h-5 w-5" />Jitsi Meet
              </button>
              <button
                onClick={() => setForm({ ...form, meeting_type: 'custom' })}
                className={`py-3 px-3 rounded-xl border-2 text-sm font-medium flex flex-col items-center gap-1.5 transition-all ${form.meeting_type === 'custom' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                data-testid="meeting-type-custom"
              >
                <LinkIcon className="h-5 w-5" />Lien perso
              </button>
              <button
                onClick={() => setForm({ ...form, meeting_type: 'none', custom_meeting_url: '' })}
                className={`py-3 px-3 rounded-xl border-2 text-sm font-medium flex flex-col items-center gap-1.5 transition-all ${form.meeting_type === 'none' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                data-testid="meeting-type-none"
              >
                <X className="h-5 w-5" />Aucune
              </button>
            </div>
            {form.meeting_type === 'jitsi' && (
              <p className="mt-2 text-xs text-indigo-600 bg-indigo-50 px-3 py-2 rounded-lg">Un lien Jitsi Meet sera genere automatiquement</p>
            )}
            {form.meeting_type === 'custom' && (
              <div className="mt-2">
                <Input
                  value={form.custom_meeting_url}
                  onChange={(e) => setForm({ ...form, custom_meeting_url: e.target.value })}
                  placeholder="https://teams.microsoft.com/... ou https://zoom.us/..."
                  data-testid="custom-meeting-url"
                />
                <p className="mt-1 text-xs text-gray-400">Collez votre lien Teams, Zoom, Google Meet, etc.</p>
              </div>
            )}
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Visibilite</label>
            <div className="flex gap-3">
              <button onClick={() => setForm({ ...form, visibility: 'members' })} className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium flex items-center justify-center gap-2 transition-all ${form.visibility === 'members' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'}`}><Lock className="h-4 w-4" />Membres</button>
              <button onClick={() => setForm({ ...form, visibility: 'public' })} className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium flex items-center justify-center gap-2 transition-all ${form.visibility === 'public' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600'}`}><Globe className="h-4 w-4" />Public</button>
            </div>
          </div>
        </div>
        <div className="p-6 border-t flex gap-3 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button onClick={handleSave} disabled={saving || !form.title.trim() || !form.date} className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white" data-testid="save-event-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}Creer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CalendarTab;
